import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import ReCaptcha from 'react-google-recaptcha';
import { Grid } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useSelector } from 'react-redux';

import { RailzButton } from '@railzai/railz-uikit-react';

import useStyle from '../style';

import { isAuthLoading } from '../../../store/features/auth';

import { EMAIL_TEAM_INVALID_DOMAINS_REGEX } from 'helpers/regex.helper';
import { FormInput } from 'components/form';
import i18n from 'translations';
import config from 'config';
import { ENVIRONMENT_TYPES } from 'types';

interface Data {
  email: string;
  captchaToken: string;
}

const ENV_LIST = [ENVIRONMENT_TYPES.QA2] as string[];

interface Props {
  login: (data: Omit<Data, 'captchaToken'>) => any;
  isCodeVerifying?: boolean;
}

const initialValues: Data = {
  email: sessionStorage.getItem('auth-email') || '',
  captchaToken: '',
};

export const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email('DASHBOARD_LOGIN_EMAIL_REQUIRED')
    .required('DASHBOARD_LOGIN_EMAIL_REQUIRED')
    .nullable()
    .matches(EMAIL_TEAM_INVALID_DOMAINS_REGEX, 'DASHBOARD_EMAIL_NOT_WORK_EMAIL'),
  captchaToken: yup.lazy((value) =>
    value === undefined
      ? yup.string().nullable()
      : yup.string().required(i18n.t('DASHBOARD_VERIFY_HUMAN')).nullable(),
  ),
});

export default function LoginForm({ login, isCodeVerifying = false }: Props): JSX.Element {
  const classes = useStyle();
  const { t } = useTranslation();
  const { handleSubmit, errors, formState, control, register, setValue, trigger } = useForm({
    mode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });
  const [showCaptchaInput, setShowCaptchaInput] = useState(false);
  const isLoading = useSelector(isAuthLoading) || false;

  const onSubmit = async (data: Omit<Data, 'captchaToken'>): Promise<void> => {
    login({ email: data.email });
  };

  useEffect(() => {
    if (!ENV_LIST.includes(config.DD_RUM_ENVIRONMENT)) {
      setShowCaptchaInput(true);
      register({ name: 'captchaToken' });
    }
  }, []);

  const onVerifyCaptcha = (token): void => {
    setValue('captchaToken', token);
    trigger('captchaToken');
  };

  const { isSubmitting } = formState;
  const isFormDisabled = isSubmitting || isLoading || isCodeVerifying;

  return (
    <div style={{ paddingTop: '32px' }}>
      <form data-testid="form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid xs={12} item>
            <FormInput
              name="email"
              type="email"
              placeholder="DASHBOARD_SIGNIN_EMAIL_PLACEHOLDER"
              label="DASHBOARD_SIGNIN_EMAIL_LABEL"
              testid="email-input"
              fullWidth
              errorobj={errors}
              control={control}
              disabled={isCodeVerifying}
            />
          </Grid>
        </Grid>
        {/* To be hidden on QA2 */}
        {showCaptchaInput && !isCodeVerifying && (
          <div className={classes.captchaContainer}>
            <ReCaptcha
              data-testid="captcha-input"
              sitekey={config.RECAPTCHA_KEY}
              onChange={onVerifyCaptcha}
            />
            {errors.captchaToken && (
              <p className={classes.captchaError}>{errors.captchaToken.message}</p>
            )}
          </div>
        )}

        <RailzButton
          size="large"
          buttonType="submit"
          isDisabled={isFormDisabled}
          data-testid="submit"
          loading={isLoading || isCodeVerifying}
          label={t('DASHBOARD_SIGNIN_CTA')}
          className={classes.loginButton}
        />
      </form>
    </div>
  );
}
